import { useMediaQuery, useTheme } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Loading from "../../../components/Loading";
import NoData from "../../../components/UI/NoData";
import useFetch from "../../../hooks/useFetch";
import { ScheduleAppointmentService } from "../appointment.service";
import { IScheduleCheckInRule } from "../model";
import ScheduleList from "./ScheduleList";
import { getMySchedulesPortal } from "../../../services/mySchedulesPortal.service";
import { ISchdulesProps, IScheduleProps } from "./models";
import SchedulesTable from "./SchedulesTable";

const Schedules: React.FC<ISchdulesProps> = ({
  historic = false,
  isLinkAccess = false,
}) => {
  const [schedules, setSchedules] = useState<IScheduleProps>({
    isLinkAccess,
    data: [],
  });

  const [checkInRule, setCheckInRule] = useState<IScheduleCheckInRule | null>(
    null,
  );

  const { t } = useTranslation();

  const { sendRequest: scheduleAppointmentListRequest, loading: sLoading } =
    useFetch(ScheduleAppointmentService.getList);

  const { sendRequest: getMySchedulesPortalRequest, loading: gLoading } =
    useFetch(getMySchedulesPortal);

  const { breakpoints } = useTheme();

  const mobile = useMediaQuery(breakpoints.down("md"));

  const fetchScheduleAppointmentList = useCallback(async () => {
    const { data, success } = await scheduleAppointmentListRequest(historic);

    if (data && success) {
      setSchedules({
        isLinkAccess: false,
        data: data.calendarConsultationExamScheduleAppointment,
      });
      setCheckInRule(data.tasySchedCheckInRule);
    }
  }, [scheduleAppointmentListRequest, historic]);

  const fetchMySchedulesPortalRequest = useCallback(async () => {
    const { data, success } = await getMySchedulesPortalRequest({ historic });

    if (data && success) {
      setSchedules({
        isLinkAccess: true,
        data: data,
      });
    }
  }, [getMySchedulesPortalRequest, historic]);

  useEffect(() => {
    if (isLinkAccess) {
      fetchMySchedulesPortalRequest();
    } else {
      fetchScheduleAppointmentList();
    }
  }, [
    isLinkAccess,
    fetchMySchedulesPortalRequest,
    fetchScheduleAppointmentList,
  ]);

  const loading = sLoading || gLoading;

  if (loading) {
    return <Loading />;
  }

  if (schedules.data.length === 0) {
    return <NoData message={t("page.schedules.home.noFound")} />;
  }

  if (mobile) {
    return (
      <ScheduleList
        schedules={schedules}
        checkInRule={checkInRule!}
        isHistoric={historic}
      />
    );
  }

  return (
    <SchedulesTable
      schedules={schedules}
      checkInRule={checkInRule!}
      isHistoric={historic}
    />
  );
};

export default memo(Schedules);
